import * as React from "react";
import { CssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";

import Sidebar from "../components/VendorSidebar";
import { SummaryStatCard } from "../components/dashboard-summary/SummaryStatCard";
import { CircularProgress, Sheet, Typography } from "@mui/joy";
import "./Dashboard.css";
import VendorInfoCard from "../components/vendor-details/VendorInfoCard";
import { muiThemeFinal } from "../utils/constants";
import { PieChartWithLegend } from "../components/SimplePieChart";
import { ThemeProvider as MaterialThemeProvider, createTheme as createMaterialTheme } from "@mui/material/styles";

import { useGetVendorStatsQuery, useGetVendorDetailsWithIdQuery } from "../services/vendor/VendorApi";

import { Backdrop } from "@mui/material";
import VendorAmountCard from "../components/Vendor/VendorAmountCard";

interface IAmountData {
  paid_amount: string;
  pending_amount: string;
  total_amount: string;
}

export default function VendorDashboard() {
  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const {
    data: vendorStats,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetVendorStatsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const current_vendor_id: any = localStorage.getItem("vendor_code");
  const { data: vendor_details } = useGetVendorDetailsWithIdQuery(current_vendor_id);

  if (isLoading) {
    return (
      <Backdrop sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })} open={isLoading}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (!vendorStats) {
    return <Typography sx={{ p: 2 }}>No vendor statistics data available.</Typography>;
  }

  const invoice_counts: any = vendorStats["invoice_counts"];
  const invoice_amounts: any = { ...vendorStats["invoice_amounts"] };

  const invoicesData = [
    {
      label: "Total Invoices",
      value: invoice_counts?.total,
      color: "#0088FE",
    },
    {
      label: "Accepted Invoices",
      value: invoice_counts?.accepted,
      color: "#00C49F",
    },
    {
      label: "Paid Invoices",
      value: invoice_counts?.paid,
      color: "#FFBB28",
    },
    {
      label: "Rejected Invoices",
      value: invoice_counts?.rejected,
      color: "#ff4242ff",
    },
    {
      label: "Submitted Invoices",
      value: invoice_counts?.invoice_submitted,
      color: "#ff42b7ff",
    },
  ];

  const billsData = [
    {
      label: "Submitted Invoices",
      value: invoice_counts?.amounts?.total_amount,
      color: "#0088FE",
    },
    {
      label: "Paid Invoices",
      value: invoice_counts?.amounts?.paid_amount,
      color: "#00C49F",
    },
    {
      label: "Pending Invoices",
      value: invoice_counts?.amounts?.pending_amount,
      color: "#FFBB28",
    },
  ];

  const headerStyles = {
    bgcolor: "white", // Teal-like header background
    color: "primary.plainColor",
    px: { xs: 2, sm: 3 },
    textAlign: "center",
    borderRadius: "lg", // Match outer container
  };

  const responseVendorData = {
    companyName: vendor_details?.name || "",
    vendorNo: vendor_details?.lifnr || "",
    address: vendor_details?.streetHouse || "",
    panNoLabel: "Pan No.",
    panNoValue: vendor_details?.pan || "",
    bankKeyLabel: "Bank Key",
    bankKeyValue: vendor_details?.bankKey || "",
    gstLabel: "GST",
    gstValue: vendor_details?.taxNumber || "",
    accountNoLabel: "A/C",
    accountNoValue: vendor_details?.bankAccount || "",
  };

  return (
    <CssVarsProvider disableTransitionOnChange>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "92dvh", background: "white" }}>
        {/* <Header /> */}
        <Sidebar />
        <Box sx={{ width: "100vw" }}>
          {/* Optional Header Section like in the screenshot */}
          <Sheet sx={headerStyles}>
            <Box sx={{ p: 0, bgcolor: "white" }}>
              <MaterialThemeProvider theme={muiThemeFinal}>
                {/* Optional: MaterialCssBaseline if you want its specific resets for this section */}
                {/* <MaterialCssBaseline /> */}
                <Box
                  sx={{
                    p: 3,
                    bgcolor: "var(--joy-palette-background-surface)" /* Use Joy CSS var */,
                  }}
                >
                  <VendorInfoCard vendor={responseVendorData} />
                </Box>
              </MaterialThemeProvider>
            </Box>
          </Sheet>

          <div className="container-outer">
            <div className="container-one">
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center" }}>
                <div>
                  {/* Responsive grid columns */}
                  <SummaryStatCard value={invoice_counts.total} label="Total Invoices" valueColor="primary" />
                </div>
                <div>
                  <SummaryStatCard value={invoice_counts.accepted} label="Accepted Invoices" valueColor="success" />
                </div>
                <div>
                  <SummaryStatCard value={invoice_counts.paid} label="Paid Invoices" valueColor="warning" />
                </div>
                <div>
                  <SummaryStatCard value={invoice_counts.rejected} label="Rejected Invoices" valueColor="danger" />
                </div>
                <div>
                  <SummaryStatCard value={invoice_counts.invoice_submitted} label="Submitted Invoices" valueColor="danger" />
                </div>
              </div>
              <Box sx={{ p: 2 }}>
                <Typography level="h3" component="h1" textAlign="center" mb={2} className="heading-2">
                  Invoices Data (Quantity)
                </Typography>
                <PieChartWithLegend data={invoicesData} />
              </Box>
            </div>

            <VendorAmountCard amounts={invoice_amounts} />
          </div>
        </Box>
      </Box>
    </CssVarsProvider>
  );
}
